import router from '@/router';
import mixpanel from 'mixpanel-browser'

/**
 * Mixpanel 工具类
 * 统一管理用户识别、属性设置和重置等功能
 */
class MixpanelService {
  private isInitialized = false;
  private hasValidToken = false;

  private getChannelFromSession(): string | undefined {
    if (typeof window === 'undefined') {
      return undefined
    }
    try {
      const channel = window.sessionStorage?.getItem('channel')
      const trimmed = channel?.trim()
      return trimmed || undefined
    } catch (error) {
      console.warn('Mixpanel: 读取 channel 失败', error)
      return undefined
    }
  }

  async init(token: string) {
    // 检查 token 是否有效
    if (!token || token === 'undefined' || token.trim() === '') {
      console.warn('Mixpanel: 无效的 token，跳过初始化');
      this.isInitialized = false;
      this.hasValidToken = false;
      return;
    }

    try {
      const isWeChat = () => /micromessenger/i.test(navigator.userAgent);
      mixpanel.init(token, {
        debug: !import.meta.env.PROD,
        ignore_dnt: !import.meta.env.PROD,
        persistence: "localStorage",
        track_pageview: false
      });
      
      // 只有在成功初始化后才进行后续操作
      this.isInitialized = true;
      this.hasValidToken = true;
      
      await router.isReady();
      const registerPayload: Record<string, any> = {
        page_path: router.currentRoute.value.path,
        language: navigator.language,
        ua: navigator.userAgent,
        is_wechat_browser: isWeChat(),
      }
      const initialChannel = this.getChannelFromSession()
      if (initialChannel) {
        registerPayload.channel = initialChannel
      }
      mixpanel.register(registerPayload);

      mixpanel.track_pageview();
      router.afterEach((to, from) => {
        if (to.path === from.path) {
          return;
        };
        if (this.isInitialized && this.hasValidToken) {
          const afterEachPayload: Record<string, any> = {
            page_path: router.currentRoute.value.path,
          }
          const channel = this.getChannelFromSession()
          if (channel) {
            afterEachPayload.channel = channel
          }
          mixpanel.register(afterEachPayload);
          mixpanel.track_pageview();
        }
      })
      
      console.log('Mixpanel: 初始化成功');
    } catch (error) {
      console.error('Mixpanel: 初始化失败', error);
      this.isInitialized = false;
      this.hasValidToken = false;
    }
  }

  /**
   * 检查是否可以安全使用 mixpanel
   */
  private canUseMixpanel(): boolean {
    return this.isInitialized && this.hasValidToken;
  }

  /**
   * 识别用户
   * @param userId 用户ID
   * @param userProperties 用户属性（可选）
   */
  identify(userId: string, userProperties?: Record<string, any>) {
    if (!this.canUseMixpanel()) {
      console.warn('Mixpanel: 未正确初始化，跳过 identify 操作');
      return;
    }

    try {
      // 识别用户
      mixpanel.identify(userId)

      // 如果提供了用户属性，则设置用户属性
      if (userProperties) {
        this.setUserProperties(userProperties)
      }

      console.log('Mixpanel: User identified', { userId, userProperties })
    } catch (error) {
      console.error('Mixpanel identify error:', error)
    }
  }

  /**
   * 设置用户属性
   * @param properties 用户属性对象
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.canUseMixpanel()) {
      console.warn('Mixpanel: 未正确初始化，跳过 setUserProperties 操作');
      return;
    }

    try {
      mixpanel.people.set(properties)
      console.log('Mixpanel: User properties set', properties)
    } catch (error) {
      console.error('Mixpanel set user properties error:', error)
    }
  }

  /**
   * 增量设置用户属性（数值类型）
   * @param properties 要增加的属性对象
   */
  incrementUserProperties(properties: Record<string, number>) {
    if (!this.canUseMixpanel()) {
      console.warn('Mixpanel: 未正确初始化，跳过 incrementUserProperties 操作');
      return;
    }

    try {
      mixpanel.people.increment(properties)
      console.log('Mixpanel: User properties incremented', properties)
    } catch (error) {
      console.error('Mixpanel increment user properties error:', error)
    }
  }

  /**
   * 追加用户属性（数组类型）
   * @param properties 要追加的属性对象
   */
  appendUserProperties(properties: Record<string, any>) {
    if (!this.canUseMixpanel()) {
      console.warn('Mixpanel: 未正确初始化，跳过 appendUserProperties 操作');
      return;
    }

    try {
      mixpanel.people.append(properties)
      console.log('Mixpanel: User properties appended', properties)
    } catch (error) {
      console.error('Mixpanel append user properties error:', error)
    }
  }

  /**
   * 追踪事件
   * @param eventName 事件名称
   * @param properties 事件属性（可选）
   */
  track(eventName: string, properties?: Record<string, any>) {
    if (!this.canUseMixpanel()) {
      console.warn('Mixpanel: 未正确初始化，跳过 track 操作');
      return;
    }

    try {
      let enhancedProperties = properties
      if (typeof window !== 'undefined' && window.sessionStorage) {
        const channel = window.sessionStorage.getItem('channel')
        if (channel && channel.trim()) {
          enhancedProperties = {
            ...(properties || {}),
            channel: channel.trim(),
          }
        }
      }

      mixpanel.track(eventName, enhancedProperties)
      console.log('Mixpanel: Event tracked', { eventName, properties })
    } catch (error) {
      console.error('Mixpanel track error:', error)
    }
  }

  /**
   * 重置用户状态（通常在用户登出时调用）
   */
  reset() {
    if (!this.canUseMixpanel()) {
      console.warn('Mixpanel: 未正确初始化，跳过 reset 操作');
      return;
    }

    try {
      mixpanel.reset()
      console.log('Mixpanel: User reset')
    } catch (error) {
      console.error('Mixpanel reset error:', error)
    }
  }

  /**
   * 获取当前用户的 distinct_id
   */
  getDistinctId(): string {
    if (!this.canUseMixpanel()) {
      console.warn('Mixpanel: 未正确初始化，跳过 getDistinctId 操作');
      return '';
    }

    try {
      return mixpanel.get_distinct_id()
    } catch (error) {
      console.error('Mixpanel get distinct id error:', error)
      return ''
    }
  }

  /**
   * 设置用户别名（用于关联匿名用户和已识别用户）
   * @param alias 用户别名
   */
  alias(alias: string) {
    if (!this.canUseMixpanel()) {
      console.warn('Mixpanel: 未正确初始化，跳过 alias 操作');
      return;
    }

    try {
      mixpanel.alias(alias)
      console.log('Mixpanel: User alias set', alias)
    } catch (error) {
      console.error('Mixpanel alias error:', error)
    }
  }
}

// 创建单例实例
const mixpanelService = new MixpanelService()

export default mixpanelService
export { MixpanelService }